import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui"
import type { CreateCreativeFlagRequest } from "@nexus/contracts"
import { getLocalUserFacingApiMessage } from "@/lib/api-client"
import {
  useCreativeFlags,
  useDeleteCreativeFlag,
} from "@/features/opportunities/hooks/useCreativeFlags"
import { CreateFlagDialog } from "../CreateFlagDialog/CreateFlagDialog"
import { opportunityKeys, creativeFlagKeys } from "@/lib/queries"
import { PAGE_SIZE } from "../OpportunityCreatives/OpportunityCreatives"

// Reuse a stable formatter (same pattern as CreativeCard to avoid re-instantiation).
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
})

function formatDate(iso: string): string {
  return DATE_FORMAT.format(new Date(iso))
}

export interface ViewFlagsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  creativeHash: string
  /** The create-context for "Add another" — same shape as CreateFlagDialog's report prop. */
  report: Omit<CreateCreativeFlagRequest, "reason">
  /** Passed explicitly from CreativeCard so we don't re-derive from report.context. */
  opportunityId: string
}

export function ViewFlagsDialog({
  open,
  onOpenChange,
  creativeHash,
  report,
  opportunityId,
}: ViewFlagsDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [addAnotherOpen, setAddAnotherOpen] = useState(false)

  const { data: flags, isLoading } = useCreativeFlags(creativeHash, open)
  const deleteFlag = useDeleteCreativeFlag()

  function invalidateBoth() {
    void queryClient.invalidateQueries({
      queryKey: creativeFlagKeys.byHash(creativeHash),
    })
    if (opportunityId) {
      void queryClient.invalidateQueries({
        queryKey: opportunityKeys.creativesInfinite(opportunityId, PAGE_SIZE),
      })
    }
  }

  function handleDelete(id: string) {
    deleteFlag.mutate(id, {
      onSuccess: () => {
        toast.success(t("opportunities.drawer.flagViewDeleteSuccess"))
        invalidateBoth()
      },
      onError: (err) => {
        const message = getLocalUserFacingApiMessage(err)
        if (message) toast.error(message)
      },
    })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("opportunities.drawer.flagViewTitle")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {isLoading ? (
              <p className="text-sm text-muted-foreground">
                {t("opportunities.drawer.flagViewLoading")}
              </p>
            ) : !flags || flags.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("opportunities.drawer.flagViewEmpty")}
              </p>
            ) : (
              flags.map((flag) => (
                <div
                  key={flag.id}
                  className="flex items-start justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-semibold">{flag.contextLabel}</p>
                    <p className="text-sm">{flag.reason}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("opportunities.drawer.flagViewBy", { name: flag.reporterName })}
                      {" · "}
                      <span data-testid="reporter-email">{flag.reporterEmail}</span>
                      {" · "}
                      {formatDate(flag.createdAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label={t("opportunities.drawer.flagViewDelete")}
                    className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:text-destructive"
                    onClick={() => handleDelete(flag.id)}
                    disabled={deleteFlag.isPending}
                  >
                    <Icon name="trash-2" size="xs" color="inherit" />
                  </button>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddAnotherOpen(true)}
            >
              {t("opportunities.drawer.flagViewAddAnother")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embedded CreateFlagDialog for "Add another" */}
      <CreateFlagDialog
        open={addAnotherOpen}
        onOpenChange={setAddAnotherOpen}
        report={report}
        onCreated={() => {
          invalidateBoth()
        }}
      />
    </>
  )
}
